/** Due/start date picker with ClickUp's quick picks, a month calendar, "Add time" and "Set Recurring". */
import { Suspense, lazy, useMemo, useState, type ReactNode } from 'react';
import { CalendarDays, ChevronDown, ChevronRight, ChevronUp, Repeat, X } from 'lucide-react';
import {
  addDays,
  addMinutes,
  dayOfWeek,
  formatMonthDay,
  formatShortDate,
  formatTime12,
  parseISODate,
  toLocalDate,
  toLocalTime,
  todayInTz,
  weekdayShort,
  type ISODate,
  type ISOTime,
  type Status,
} from '@karya/core';
import { cn } from '@/lib/cn';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/overlay';
import type { RecurrenceValue } from './recurrence-editor';

const RecurrenceEditor = lazy(() =>
  import('./recurrence-editor').then((m) => ({ default: m.RecurrenceEditor })),
);

export interface DateValue {
  start_date: ISODate | null;
  due_date: ISODate | null;
  due_time: ISOTime | null;
}

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

function quickPicks(now: Date, tz: string) {
  const today = todayInTz(tz, now);
  const dow = dayOfWeek(today);
  const sat = addDays(today, dow === 6 ? 7 : 6 - dow);
  const mon = addDays(today, dow === 1 ? 7 : (8 - dow) % 7 || 7);
  const later = addMinutes(now, 4 * 60);
  const laterRounded = new Date(Math.ceil(later.getTime() / (15 * 60_000)) * 15 * 60_000);
  return [
    { label: 'Today', date: today, hint: weekdayShort(today) },
    {
      label: 'Later',
      date: toLocalDate(laterRounded, tz),
      time: toLocalTime(laterRounded, tz),
      hint: formatTime12(toLocalTime(laterRounded, tz)),
    },
    { label: 'Tomorrow', date: addDays(today, 1), hint: weekdayShort(addDays(today, 1)) },
    { label: 'This weekend', date: sat, hint: weekdayShort(sat) },
    { label: 'Next week', date: mon, hint: weekdayShort(mon) },
    { label: 'Next weekend', date: addDays(sat, 7), hint: formatMonthDay(addDays(sat, 7)) },
    { label: '2 weeks', date: addDays(today, 14), hint: formatMonthDay(addDays(today, 14)) },
    { label: '4 weeks', date: addDays(today, 28), hint: formatMonthDay(addDays(today, 28)) },
  ];
}

function MonthGrid({
  month,
  today,
  start,
  due,
  onPick,
}: {
  month: { y: number; m: number };
  today: ISODate;
  start: ISODate | null;
  due: ISODate | null;
  onPick: (d: ISODate) => void;
}) {
  const first = `${month.y}-${String(month.m).padStart(2, '0')}-01`;
  const lead = dayOfWeek(first);
  const days = Array.from({ length: 42 }, (_, i) => addDays(first, i - lead));
  return (
    <div
      className="grid grid-cols-7 gap-y-0.5 text-center text-sm"
      role="grid"
      aria-label={`${MONTHS[month.m - 1]} ${month.y}`}
    >
      {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d) => (
        <div key={d} className="py-1 text-cap text-ink-3">
          {d}
        </div>
      ))}
      {days.map((d) => {
        const inMonth = parseISODate(d).month === month.m;
        const isDue = d === due;
        const isStart = d === start;
        const inRange = start && due && d > start && d < due;
        return (
          <button
            key={d}
            onClick={() => onPick(d)}
            aria-label={d}
            aria-pressed={isDue || isStart}
            className={cn(
              'mx-auto flex size-7 items-center justify-center rounded-full tabular',
              !inMonth && 'text-ink-3',
              inRange && 'rounded-none bg-primary-soft',
              (isDue || isStart) && 'bg-primary font-semibold text-on-primary',
              !isDue && !isStart && d === today && 'font-semibold text-overdue ring-1 ring-overdue',
              !isDue && !isStart && !inRange && 'hover:bg-surface-2',
            )}
          >
            {parseISODate(d).day}
          </button>
        );
      })}
    </div>
  );
}

export function DatePicker({
  value,
  onChange,
  tz,
  now = new Date(),
  showStart = true,
  recurrence,
  onRecurrenceChange,
  statuses = [],
  children,
  align = 'start',
}: {
  value: DateValue;
  onChange: (patch: Partial<DateValue>) => void;
  tz: string;
  now?: Date;
  showStart?: boolean;
  recurrence?: RecurrenceValue | null;
  onRecurrenceChange?: (v: RecurrenceValue | null) => void;
  statuses?: Status[];
  children: ReactNode;
  align?: 'start' | 'end';
}) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<'start' | 'due'>('due');
  const [showTime, setShowTime] = useState(Boolean(value.due_time));
  const [recurringOpen, setRecurringOpen] = useState(false);
  const today = todayInTz(tz, now);
  const anchor = value.due_date ?? today;
  const [month, setMonth] = useState(() => ({
    y: parseISODate(anchor).year,
    m: parseISODate(anchor).month,
  }));
  const picks = useMemo(() => quickPicks(now, tz), [now, tz]);

  const pick = (d: ISODate, time?: ISOTime) => {
    if (editing === 'start') {
      onChange({ start_date: d });
      setEditing('due');
      return;
    }
    onChange({ due_date: d, ...(time ? { due_time: time } : {}) });
    if (time) setShowTime(true);
  };
  const shift = (delta: number) =>
    setMonth(({ y, m }) => {
      const t = new Date(Date.UTC(y, m - 1 + delta, 1));
      return { y: t.getUTCFullYear(), m: t.getUTCMonth() + 1 };
    });

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) {
          setRecurringOpen(false);
          setEditing('due');
        }
      }}
    >
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        align={align}
        className="flex w-[min(560px,calc(100vw-16px))] flex-col p-0"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 gap-2 border-b border-line p-2">
          {showStart && (
            <DateField
              label="Start date"
              active={editing === 'start'}
              value={value.start_date ? formatShortDate(value.start_date) : null}
              onFocus={() => setEditing('start')}
              onClear={() => onChange({ start_date: null })}
            />
          )}
          <DateField
            label="Due date"
            active={editing === 'due'}
            value={value.due_date ? formatShortDate(value.due_date) : null}
            onFocus={() => setEditing('due')}
            onClear={() => onChange({ due_date: null, due_time: null })}
            time={
              value.due_date ? (showTime ? (value.due_time?.slice(0, 5) ?? '') : null) : undefined
            }
            onTime={(t) => onChange({ due_time: t || null })}
            onAddTime={() => setShowTime(true)}
          />
        </div>
        {recurringOpen && onRecurrenceChange ? (
          // min-h-0 lets the editor's options scroll while its Save row stays in view.
          <div className="flex min-h-0 flex-col p-2">
            <Suspense fallback={<div className="p-4 text-ink-3">Loading…</div>}>
              <RecurrenceEditor
                anchor={anchor}
                value={recurrence ?? null}
                statuses={statuses}
                onCancel={() => setRecurringOpen(false)}
                onRemove={() => {
                  onRecurrenceChange(null);
                  setRecurringOpen(false);
                }}
                onSave={(v) => {
                  if (!value.due_date) onChange({ due_date: anchor });
                  onRecurrenceChange(v);
                  setRecurringOpen(false);
                }}
              />
            </Suspense>
          </div>
        ) : (
          <div className="flex max-sm:flex-col">
            <div className="flex w-full flex-col border-line sm:w-52 sm:border-r">
              <div className="flex-1 py-1">
                {picks.map((p) => (
                  <button
                    key={p.label}
                    onClick={() => pick(p.date, p.time)}
                    className="flex h-7 w-full items-center justify-between px-3 text-left hover:bg-surface-2"
                  >
                    <span>{p.label}</span>
                    <span className="text-cap text-ink-3">{p.hint}</span>
                  </button>
                ))}
              </div>
              {onRecurrenceChange && (
                <button
                  onClick={() => setRecurringOpen(true)}
                  className="flex h-10 items-center gap-2 border-t border-line px-3 text-left hover:bg-surface-2"
                >
                  <Repeat className="size-4 text-ink-2" />
                  <span className="flex-1">{recurrence ? 'Edit recurring' : 'Set Recurring'}</span>
                  <ChevronRight className="size-4 text-ink-3" />
                </button>
              )}
            </div>
            <div className="flex-1 p-3">
              <div className="mb-2 flex items-center gap-2">
                <span className="font-semibold">
                  {MONTHS[month.m - 1]} {month.y}
                </span>
                <button
                  className="text-cap text-ink-3 hover:text-ink"
                  onClick={() =>
                    setMonth({ y: parseISODate(today).year, m: parseISODate(today).month })
                  }
                >
                  Today
                </button>
                <span className="flex-1" />
                <button
                  aria-label="Previous month"
                  onClick={() => shift(-1)}
                  className="rounded-sm p-1 text-ink-2 hover:bg-surface-2"
                >
                  <ChevronUp className="size-4" />
                </button>
                <button
                  aria-label="Next month"
                  onClick={() => shift(1)}
                  className="rounded-sm p-1 text-ink-2 hover:bg-surface-2"
                >
                  <ChevronDown className="size-4" />
                </button>
              </div>
              <MonthGrid
                month={month}
                today={today}
                start={value.start_date}
                due={value.due_date}
                onPick={(d) => pick(d)}
              />
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

function DateField({
  label,
  value,
  active,
  onFocus,
  onClear,
  time,
  onTime,
  onAddTime,
}: {
  label: string;
  value: string | null;
  active: boolean;
  onFocus: () => void;
  onClear: () => void;
  time?: string | null;
  onTime?: (t: string) => void;
  onAddTime?: () => void;
}) {
  return (
    <div
      className={cn(
        'flex h-9 min-w-0 flex-1 items-center gap-2 rounded-sm border px-2',
        active ? 'border-primary' : 'border-line',
      )}
    >
      <button
        onClick={onFocus}
        className="flex min-w-0 flex-1 items-center gap-2 text-left"
        aria-label={`${label}${value ? `: ${value}` : ''}`}
      >
        <CalendarDays className="size-4 shrink-0 text-ink-2" />
        <span className={cn('truncate', !value && 'text-ink-3')}>{value ?? label}</span>
      </button>
      {value && (
        <button
          onClick={onClear}
          aria-label={`Clear ${label.toLowerCase()}`}
          className="text-ink-3 hover:text-ink"
        >
          <X className="size-3.5" />
        </button>
      )}
      {time === null && onAddTime && (
        <button onClick={onAddTime} className="text-sm whitespace-nowrap text-ink-3 hover:text-ink">
          Add time
        </button>
      )}
      {typeof time === 'string' && onTime && (
        <input
          type="time"
          aria-label="Due time"
          value={time}
          onChange={(e) => onTime(e.target.value)}
          className="w-[92px] bg-transparent text-sm outline-none"
        />
      )}
    </div>
  );
}
